import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Sparkles, CheckCircle2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { api, trackEvent } from "../lib/api";

const QUESTIONS = [
  { id: 1, text: "I have felt calm and emotionally balanced.", reverse: false },
  { id: 2, text: "I have enjoyed my daily activities.", reverse: false },
  { id: 3, text: "I have felt overwhelmed by life's demands.", reverse: true },
  { id: 4, text: "I have been able to relax and switch off from stress.", reverse: false },
  { id: 5, text: "I have woken feeling rested and had enough energy for the day.", reverse: false },
  { id: 6, text: "I have been able to focus and think clearly.", reverse: false },
  { id: 7, text: "I have felt connected to people who support and care about me.", reverse: false },
  { id: 8, text: "I have made time for activities that support my wellbeing.", reverse: false },
  { id: 9, text: "I have felt confident in my ability to cope with life's challenges.", reverse: false },
  { id: 10, text: "Overall, I feel my life is balanced and aligned with what matters most to me.", reverse: false },
];

const SCALE = [
  { value: 0, label: "Never" },
  { value: 1, label: "Rarely" },
  { value: 2, label: "Sometimes" },
  { value: 3, label: "Often" },
  { value: 4, label: "Almost always" },
];

const RESULT_BUCKETS = [
  {
    min: 32, max: 40, key: "flourishing",
    title: "Flourishing",
    blurb: "You're experiencing a strong sense of wellbeing across many areas of life. Continue nurturing the habits and relationships that support you.",
    tone: "bg-sage/15 border-sage/50 text-sage-deep",
    accent: "text-sage-deep",
  },
  {
    min: 24, max: 31, key: "generally_well",
    title: "Generally well",
    blurb: "You're managing well overall, with a few areas that may benefit from more attention or self-care.",
    tone: "bg-navy/8 border-navy/30 text-navy-deep",
    accent: "text-navy-deep",
  },
  {
    min: 16, max: 23, key: "under_pressure",
    title: "Under pressure",
    blurb: "Your responses suggest that stress or other challenges may be affecting your wellbeing. Consider what changes or supports could help you feel more balanced.",
    tone: "bg-sand/40 border-sand text-ink",
    accent: "text-ink",
  },
  {
    min: 0, max: 15, key: "prioritise",
    title: "Time to prioritise your wellbeing",
    blurb: "Your responses indicate significant strain. If these experiences have persisted or are affecting your daily life, speaking with a qualified counsellor or healthcare professional may be a helpful next step.",
    tone: "bg-clay/15 border-clay/50 text-ink",
    accent: "text-clay-deep",
  },
];

function bucketForScore(score) {
  return RESULT_BUCKETS.find((b) => score >= b.min && score <= b.max) || RESULT_BUCKETS[2];
}

export default function WellbeingCheck({ open, onClose }) {
  const [step, setStep] = useState(0); // 0..9 = questions, 10 = results
  const [answers, setAnswers] = useState({});
  const [started, setStarted] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareForm, setShareForm] = useState({ name: "", email: "", message: "" });
  const [shareSubmitting, setShareSubmitting] = useState(false);
  const [shareSent, setShareSent] = useState(false);

  useEffect(() => {
    if (open && !started) {
      trackEvent("wellbeing_started", "/#wellbeing-check");
      setStarted(true);
    }
    if (!open) {
      // reset when closed
      setTimeout(() => {
        setStep(0);
        setAnswers({});
        setStarted(false);
        setShareOpen(false);
        setShareForm({ name: "", email: "", message: "" });
        setShareSent(false);
      }, 200);
    }
  }, [open, started]);

  if (!open) return null;

  const isResults = step >= QUESTIONS.length;
  const current = QUESTIONS[step];
  const progressPct = isResults ? 100 : (step / QUESTIONS.length) * 100;
  const answeredAll = QUESTIONS.every((q) => answers[q.id] !== undefined);

  const score = QUESTIONS.reduce((acc, q) => {
    const a = answers[q.id];
    if (a === undefined) return acc;
    return acc + (q.reverse ? 4 - a : a);
  }, 0);
  const bucket = bucketForScore(score);

  const handleSelect = (value) => {
    setAnswers((p) => ({ ...p, [current.id]: value }));
    // brief delay so the user sees their selection before advancing
    setTimeout(() => {
      if (step < QUESTIONS.length - 1) {
        setStep((s) => s + 1);
      } else {
        setStep(QUESTIONS.length);
        trackEvent("wellbeing_completed", `/wellbeing-check#${bucketForScore(
          QUESTIONS.reduce((acc, q) => {
            const a = q.id === current.id ? value : answers[q.id];
            if (a === undefined) return acc;
            return acc + (q.reverse ? 4 - a : a);
          }, 0)
        ).key}`);
      }
    }, 180);
  };

  const validEmail = /^\S+@\S+\.\S+$/.test(shareForm.email);
  const validName = shareForm.name.trim().length >= 2;

  const handleShareSubmit = async () => {
    if (!validName || !validEmail) return;
    setShareSubmitting(true);
    try {
      const breakdown = QUESTIONS.map((q) => {
        const a = answers[q.id];
        const scored = a === undefined ? "—" : (q.reverse ? 4 - a : a);
        const label = a === undefined ? "—" : SCALE.find((s) => s.value === a)?.label;
        return `Q${q.id} (${label}, scored ${scored}): ${q.text}`;
      }).join("\n");
      const message =
        `Wellbeing Check result — ${bucket.title} (${score}/40)\n\n` +
        (shareForm.message.trim() ? `Note from client:\n${shareForm.message.trim()}\n\n` : "") +
        `Question-by-question breakdown:\n${breakdown}\n\n` +
        `(Submitted from the public Wellbeing Check on amindfulspacecounselling.com.)`;
      await api.post("/enquiries", {
        name: shareForm.name.trim(),
        email: shareForm.email.trim(),
        subject: `Wellbeing Check · ${bucket.title} (${score}/40)`,
        message,
      });
      setShareSent(true);
      toast.success("Sent to Jesse. You'll hear back soon.");
    } catch {
      toast.error("Couldn't send right now — please try again or use the Enquiry form.");
    } finally {
      setShareSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-ink/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
        data-testid="wellbeing-overlay"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-2xl bg-cream rounded-t-[2rem] sm:rounded-[2rem] shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
          data-testid="wellbeing-modal"
        >
          {/* Header */}
          <div className="flex items-start justify-between px-7 sm:px-9 pt-7 pb-4 border-b border-sand/60">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-navy mb-2">
                <Sparkles size={14} /> Wellbeing check
              </div>
              <div className="font-serif text-xl sm:text-2xl text-ink leading-snug">
                {isResults ? "Your reflection." : "How have things felt for you?"}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="wellbeing-close"
              className="h-9 w-9 rounded-full text-ink-soft hover:bg-sand/40 hover:text-ink transition flex items-center justify-center"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Progress */}
          {!isResults && (
            <div className="px-7 sm:px-9 pt-4">
              <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">
                <span>Question {step + 1} of {QUESTIONS.length}</span>
                <span>{Math.round(progressPct)}%</span>
              </div>
              <div className="h-1.5 bg-sand/50 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-navy"
                  initial={false}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                />
              </div>
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-7 sm:px-9 py-8" data-testid="wellbeing-body">
            {!isResults ? (
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-3">Over the past two weeks…</p>
                <p
                  className="font-serif text-2xl sm:text-3xl leading-snug text-ink mb-8 text-balance"
                  data-testid="wellbeing-question"
                >
                  {current.text}
                </p>

                <div className="space-y-2.5" role="radiogroup" aria-label={current.text}>
                  {SCALE.map((opt) => {
                    const selected = answers[current.id] === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => handleSelect(opt.value)}
                        data-testid={`wellbeing-option-${current.id}-${opt.value}`}
                        className={`w-full flex items-center gap-4 rounded-2xl border px-5 py-3.5 text-left transition-all duration-200 hover:-translate-y-0.5 ${
                          selected
                            ? "border-navy bg-navy text-cream shadow-[0_10px_24px_-12px_rgba(14,59,74,0.7)]"
                            : "border-sand/70 bg-cream-soft hover:border-navy/40 text-ink"
                        }`}
                      >
                        <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-medium tabular-nums ${
                          selected ? "bg-cream text-navy" : "bg-cream text-ink-soft border border-sand/70"
                        }`}>
                          {opt.value}
                        </span>
                        <span className={`text-sm sm:text-base ${selected ? "text-cream" : "text-ink"}`}>
                          {opt.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div data-testid="wellbeing-results">
                <div className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mb-2">Your score</div>
                <div className="font-serif text-6xl text-navy leading-none mb-1 tabular-nums" data-testid="wellbeing-score">
                  {score}<span className="text-3xl text-ink-soft">/40</span>
                </div>

                <div className={`mt-6 rounded-2xl border p-6 ${bucket.tone}`} data-testid={`wellbeing-bucket-${bucket.key}`}>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] mb-2">
                    <CheckCircle2 size={14} /> Result
                  </div>
                  <div className={`font-serif text-3xl mb-3 ${bucket.accent}`}>{bucket.title}</div>
                  <p className="text-sm leading-relaxed text-ink-muted">{bucket.blurb}</p>
                </div>

                <div className="mt-7 rounded-2xl bg-navy-deep text-cream p-6">
                  <div className="font-serif text-xl mb-2">A gentle next step.</div>
                  <p className="text-sm text-cream/80 leading-relaxed mb-5 font-light">
                    If you&apos;d like to explore any of these areas further, Jesse offers
                    confidential, non-judgmental counselling — online or in person.
                    There&apos;s no obligation in reaching out.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to="/book"
                      onClick={onClose}
                      data-testid="wellbeing-cta-book"
                      className="inline-flex items-center gap-2 rounded-full bg-cream text-navy-deep px-5 py-3 text-sm font-medium hover:-translate-y-0.5 transition"
                    >
                      Book a session <ArrowRight size={14} />
                    </Link>
                    <a
                      href="/#enquiry"
                      onClick={onClose}
                      data-testid="wellbeing-cta-enquire"
                      className="inline-flex items-center gap-2 rounded-full border border-cream/40 text-cream px-5 py-3 text-sm font-medium hover:bg-cream/10 transition"
                    >
                      Send an enquiry
                    </a>
                  </div>
                </div>

                {/* OPTIONAL: send results to Jesse */}
                <div className="mt-6" data-testid="wellbeing-share-block">
                  {!shareOpen && !shareSent && (
                    <button
                      type="button"
                      onClick={() => setShareOpen(true)}
                      data-testid="wellbeing-share-toggle"
                      className="w-full inline-flex items-center justify-between gap-3 rounded-2xl border border-sand/70 bg-cream-soft hover:border-navy/40 hover:-translate-y-0.5 transition-all px-5 py-4 text-left"
                    >
                      <span className="flex items-center gap-3">
                        <span className="h-9 w-9 rounded-full bg-navy/10 text-navy flex items-center justify-center">
                          <Mail size={16} />
                        </span>
                        <span>
                          <span className="block text-sm font-medium text-ink">Send my results to Jesse (optional)</span>
                          <span className="block text-[12px] text-ink-soft mt-0.5">A private note arrives in Jesse&apos;s enquiry inbox. No automatic follow-up.</span>
                        </span>
                      </span>
                      <ArrowRight size={16} className="text-ink-soft" />
                    </button>
                  )}

                  {shareOpen && !shareSent && (
                    <div className="rounded-2xl border border-sand/70 bg-cream-soft p-5 sm:p-6" data-testid="wellbeing-share-form">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="text-[11px] uppercase tracking-[0.2em] text-navy mb-1">Send to Jesse</div>
                          <div className="font-serif text-xl text-ink leading-snug">A short, private note — just to Jesse.</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShareOpen(false)}
                          aria-label="Cancel"
                          data-testid="wellbeing-share-cancel"
                          className="h-8 w-8 rounded-full text-ink-soft hover:bg-sand/50 flex items-center justify-center"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="Your name *"
                          value={shareForm.name}
                          onChange={(e) => setShareForm({ ...shareForm, name: e.target.value })}
                          data-testid="wellbeing-share-name"
                          className="w-full rounded-xl border border-sand/70 bg-cream px-4 py-3 text-sm text-ink placeholder:text-ink-soft focus:outline-none focus:border-navy"
                        />
                        <input
                          type="email"
                          placeholder="Your email *"
                          value={shareForm.email}
                          onChange={(e) => setShareForm({ ...shareForm, email: e.target.value })}
                          data-testid="wellbeing-share-email"
                          className="w-full rounded-xl border border-sand/70 bg-cream px-4 py-3 text-sm text-ink placeholder:text-ink-soft focus:outline-none focus:border-navy"
                        />
                      </div>
                      <textarea
                        rows={3}
                        placeholder="Anything you'd like Jesse to know? (optional)"
                        value={shareForm.message}
                        onChange={(e) => setShareForm({ ...shareForm, message: e.target.value })}
                        data-testid="wellbeing-share-message"
                        className="mt-3 w-full rounded-xl border border-sand/70 bg-cream px-4 py-3 text-sm text-ink placeholder:text-ink-soft focus:outline-none focus:border-navy resize-none"
                      />
                      <p className="text-[11px] text-ink-soft mt-3 leading-relaxed">
                        Your score, bucket, and per-question answers will be included so Jesse can
                        understand the context. Your email is used only to reply to you — never marketing.
                      </p>
                      <button
                        type="button"
                        onClick={handleShareSubmit}
                        disabled={!validName || !validEmail || shareSubmitting}
                        data-testid="wellbeing-share-submit"
                        className="mt-4 inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-3 text-sm font-medium hover:bg-navy-deep disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        {shareSubmitting ? "Sending…" : <>Send to Jesse <Send size={14} /></>}
                      </button>
                    </div>
                  )}

                  {shareSent && (
                    <div
                      className="rounded-2xl border border-sage/50 bg-sage/12 px-5 py-4 flex items-start gap-3"
                      data-testid="wellbeing-share-confirmation"
                    >
                      <div className="h-9 w-9 rounded-full bg-sage/25 text-sage-deep flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={18} />
                      </div>
                      <div>
                        <div className="font-medium text-ink text-sm">Your note is on its way.</div>
                        <div className="text-[12px] text-ink-soft mt-0.5">
                          Jesse will be in touch by email. Take care of yourself in the meantime.
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-[11px] text-ink-soft mt-6 leading-relaxed">
                  This brief check is intended as a moment of reflection, not a clinical
                  diagnosis. If you&apos;re in crisis or feeling unsafe, please reach out to
                  Lifeline (13 11 14) or your nearest emergency service.
                </p>
              </div>
            )}
          </div>

          {/* Footer nav (questions only) */}
          {!isResults && (
            <div className="flex items-center justify-between px-7 sm:px-9 py-4 border-t border-sand/60 bg-cream-soft/60">
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                data-testid="wellbeing-back"
                className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-ink disabled:opacity-30 transition"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <div className="text-[11px] text-ink-soft">
                Pick an option to continue
              </div>
            </div>
          )}

          {isResults && (
            <div className="px-7 sm:px-9 py-4 border-t border-sand/60 bg-cream-soft/60 flex items-center justify-between">
              <button
                type="button"
                onClick={() => { setStep(0); setAnswers({}); }}
                data-testid="wellbeing-restart"
                className="text-sm text-ink-soft hover:text-ink underline-offset-4 hover:underline"
              >
                Take it again
              </button>
              <button
                type="button"
                onClick={onClose}
                data-testid="wellbeing-done"
                className="inline-flex items-center gap-2 rounded-full bg-ink/8 text-ink px-4 py-2 text-sm hover:bg-ink/15 transition"
              >
                Close {answeredAll && "✓"}
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
